import { useState } from "react";
import { Laptop, PcCase, Monitor, Printer, HardDrive, MemoryStick, Cpu, Router, Wifi, Server, Cable, BatteryCharging, Keyboard, Mouse,
  Headphones, Cctv, Smartphone, Tablet, AppWindow, ShieldCheck, Package, ScanLine, Projector, Phone, Webcam, Network } from "lucide-react";
import { mediaVariant } from "../lib/catalogueApi";

// T53: category -> line icon (leaf category first). Same lucide set + stroke as the header.
const RULES = [
  [/warranty|service|support|care pack|rma|onsite|maintenan/, ShieldCheck], [/software|licen|subscription|antivirus/, AppWindow],
  [/notebook|laptop|chromebook/, Laptop], [/tablet|ipad/, Tablet], [/mobile|phone|handset|dect|voip|communication/, Phone],
  [/monitor|display|screen|lcd/, Monitor], [/projector/, Projector], [/webcam|conferenc/, Webcam], [/camera|cctv|surveillance|nvr/, Cctv],
  [/scanner|barcode/, ScanLine], [/print|toner|ink|consumable|label|ribbon|3d/, Printer], [/memory|ram|dimm/, MemoryStick],
  [/hdd|ssd|hard drive|storage|nas|drive|flash|tape|sata/, HardDrive], [/cpu|processor|motherboard|component|graphics/, Cpu],
  [/server|rack/, Server], [/wireless|wifi|access point/, Wifi], [/router|modem/, Router], [/network|switch|lan|firewall/, Network],
  [/cable|adapter|adaptor|dock|hub|kvm/, Cable], [/ups|power|battery|charger|pdu/, BatteryCharging], [/keyboard/, Keyboard],
  [/mouse|mice|input/, Mouse], [/headset|headphone|audio|speaker/, Headphones], [/desktop|workstation|\bpc\b|all-in-one/, PcCase],
  [/smartphone/, Smartphone],
];
export const categoryIcon = (hint) => {
  for (const part of String(hint || "").split(">")) for (const [re, I] of RULES) if (re.test(part)) return I;
  return Package;
};

// Calm placeholder: never looks like a product photo (no fabricated imagery). Icecat-sourced brand logo (greyed), else brand
// name as text, else the category icon; always captioned "No photo".
export const NoPhoto = ({ name, brand, hint, logoId, size = "sm", className = "", testid = "no-photo" }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const Icon = categoryIcon(hint);
  const lg = size === "lg", xs = size === "xs";
  return (
    <div role="img" aria-label={name ? `${name}: no photo` : "No photo"} data-testid={testid} data-placeholder={logoId && !logoFailed ? "brand-logo" : brand && !xs ? "brand-name" : "category-icon"}
      className={`rs-nophoto flex h-full w-full flex-col items-center justify-center gap-2 rounded-[inherit] bg-slate-50 text-slate-400 ${className}`}>
      {logoId && !logoFailed ? (
        <img src={mediaVariant(logoId, 160, "png")} alt="" onError={() => setLogoFailed(true)} className={`${lg ? "max-h-14 max-w-[45%]" : "max-h-8 max-w-[60%]"} object-contain opacity-50 grayscale`} />
      ) : brand && !xs ? (
        <><Icon size={lg ? 40 : 26} strokeWidth={1.25} aria-hidden="true" className="text-slate-300" />
          <span className={`max-w-[85%] truncate font-semibold uppercase tracking-[0.14em] text-slate-400 ${lg ? "text-sm" : "text-[10px]"}`}>{brand}</span></>
      ) : <Icon size={xs ? 20 : lg ? 44 : 28} strokeWidth={1.25} aria-hidden="true" className="text-slate-300" />}
      {!xs && <span className={`${lg ? "text-xs" : "text-[10px]"} font-medium text-slate-400`}>No photo</span>}
    </div>
  );
};

// One product image frame everywhere: 1:1 white tile, even padding, soft border.
export const ProductTile = ({ src, srcSet, webp, sizes, alt = "", name, brand, hint, logoId, size = "sm", className = "", imgClass = "", testid, eager, imgTestid, nophotoTestid, ...img }) => {
  const [failed, setFailed] = useState(false);
  const el = src && !failed && (
    <img src={src} srcSet={srcSet} sizes={sizes} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : "auto"}
      onError={() => setFailed(true)} className={`h-full w-full object-contain ${imgClass}`} data-testid={imgTestid} {...img} />
  );
  return (
    <div className={`rs-tile ${size === "xs" ? "rs-tile-xs" : size === "lg" ? "rs-tile-lg" : ""} ${className}`} data-testid={testid}>
      {el ? (webp ? <picture className="contents"><source type="image/webp" srcSet={webp} sizes={sizes} />{el}</picture> : el)
        : <NoPhoto name={name} brand={brand} hint={hint} logoId={logoId} size={size} testid={nophotoTestid || (testid ? `${testid}-nophoto` : "no-photo")} />}
    </div>
  );
};
export const tileProps = (p) => ({ name: p.display_name, brand: p.brand_name || p.brand?.display_name, hint: p.category_hint, logoId: p.brand_logo_id });
